import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Crown, Calendar, Trophy, Target, BookOpen, TrendingUp, 
  ArrowLeft, Play, CheckCircle, Star, Zap, BarChart3,
  Users, Heart, MessageSquare, Lightbulb, Award, User
} from 'lucide-react';
import PersonalityProfile from './PersonalityProfile';
import axios from 'axios';

const CoachingDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCoachingDashboard();
  }, []);

  const fetchCoachingDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('✅ Coaching dashboard data loaded:', response.data);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching coaching dashboard:', error);
      if (error.response?.status === 402) {
        // Not VIP - redirect to upgrade
        navigate('/subscription/plans');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (moduleId) => {
    console.log('🎯 Module clicked:', moduleId);
    navigate(`/coaching/modules/${moduleId}`);
  };

  const handleLessonClick = (lessonId) => {
    navigate(`/coaching/lessons/${lessonId}`);
  };

  const handleCreateChallenge = async (challengeType = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      console.log('🎯 Creating challenge:', challengeType || 'auto-select');
      
      const url = challengeType 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/coaching/challenges?challenge_type=${challengeType}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/coaching/challenges`;

      const response = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      console.log('✅ Challenge created successfully:', response.data);
      
      // Refresh dashboard data to show new challenge
      fetchCoachingDashboard();
      
    } catch (error) {
      console.error('❌ Error creating challenge:', error);
      if (error.response?.status === 402) {
        navigate('/subscription/plans');
      }
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'from-green-500 to-emerald-500';
    if (percentage >= 50) return 'from-blue-500 to-cyan-500';
    if (percentage >= 25) return 'from-yellow-500 to-orange-500';
    return 'from-rose-500 to-pink-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white">Loading your coaching dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">VIP Coaching Required</h2>
          <p className="text-gray-300 mb-6">Access advanced VIP features and tools</p>
          <Button 
            onClick={() => navigate('/subscription/plans')}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
          >
            Upgrade to VIP
          </Button>
        </div>
      </div>
    );
  }

  const { coaching_progress, dating_analytics, active_challenges } = dashboardData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Crown className="mr-3 text-yellow-500" size={32} />
              VIP Features Dashboard
            </h1>
            <p className="text-gray-300 mt-1">Advanced tools for your dating journey</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => navigate('/account')}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <User className="mr-2" size={16} />
              View Profile
            </Button>
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Crown className="w-4 h-4 mr-1" />
              VIP
            </Badge>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'modules', label: 'Modules', icon: BookOpen },
            { id: 'challenges', label: 'Challenges', icon: Target },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={
                activeTab === tab.id 
                  ? 'bg-rose-500 hover:bg-rose-600' 
                  : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
              }
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Progress Overview */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{coaching_progress.coaching_stats.coaching_level}</div>
                  <div className="text-gray-400 text-sm">Coaching Level</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <Star className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{coaching_progress.coaching_stats.coaching_points}</div>
                  <div className="text-gray-400 text-sm">Coaching Points</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <Zap className="w-8 h-8 text-orange-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{coaching_progress.coaching_stats.streak_days}</div>
                  <div className="text-gray-400 text-sm">Day Streak</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <BookOpen className="w-8 h-8 text-green-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{coaching_progress.overall_progress.lessons_completed}</div>
                  <div className="text-gray-400 text-sm">Lessons Completed</div>
                </CardContent>
              </Card>
            </div>

            {/* Overall Progress */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <TrendingUp className="mr-2 text-rose-400" size={24} />
                  Overall Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-white mb-2">
                      <span>Coaching Completion</span>
                      <span>{Math.round(coaching_progress.overall_progress.completion_percentage)}%</span>
                    </div>
                    <Progress 
                      value={coaching_progress.overall_progress.completion_percentage} 
                      className="h-3"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm mb-1">Modules Progress</div>
                      <div className="text-white font-semibold">
                        {coaching_progress.overall_progress.modules_completed} / {coaching_progress.overall_progress.total_modules}
                      </div>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-gray-400 text-sm mb-1">Total Coaching Hours</div>
                      <div className="text-white font-semibold">
                        {coaching_progress.coaching_stats.total_hours.toFixed(1)} hours
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  <Button
                    onClick={() => setActiveTab('modules')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 p-6 h-auto flex-col"
                  >
                    <BookOpen className="w-8 h-8 mb-2" />
                    <span className="font-semibold">Continue Learning</span>
                    <span className="text-xs opacity-80">Resume coaching modules</span>
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/coaching/action-plan')}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 p-6 h-auto flex-col"
                  >
                    <Target className="w-8 h-8 mb-2" />
                    <span className="font-semibold">Action Plan</span>
                    <span className="text-xs opacity-80">Get personalized goals</span>
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('challenges')}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 p-6 h-auto flex-col"
                  >
                    <Trophy className="w-8 h-8 mb-2" />
                    <span className="font-semibold">Take Challenge</span>
                    <span className="text-xs opacity-80">Earn coaching points</span>
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/account')}
                    className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 p-6 h-auto flex-col"
                  >
                    <User className="w-8 h-8 mb-2" />
                    <span className="font-semibold">View Profile</span>
                    <span className="text-xs opacity-80">Manage your account</span>
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/compatibility/reports')}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 p-6 h-auto flex-col"
                  >
                    <Heart className="w-8 h-8 mb-2" />
                    <span className="font-semibold">Compatibility Reports</span>
                    <span className="text-xs opacity-80">Relationship analysis</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Personality Profile Section */}
            <PersonalityProfile />
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaching_progress.modules.map((module) => (
              <Card 
                key={module.module_id}
                className="bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer"
                onClick={() => handleModuleClick(module.module_id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl">{module.icon}</div>
                    {module.is_completed && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-white text-lg">{module.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 text-sm mb-4">{module.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-white mb-2">
                      <span className="text-sm">Progress</span>
                      <span className="text-sm">{Math.round(module.completion_percentage)}%</span>
                    </div>
                    <Progress 
                      value={module.completion_percentage} 
                      className="h-2"
                    />
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">
                      {module.lessons_completed} / {module.total_lessons} lessons
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        handleModuleClick(module.module_id);
                      }}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      {module.lessons_completed > 0 ? 'Continue' : 'Start'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="space-y-6">
            {/* Challenge Creation Options */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-yellow-400" />
                  Create New Challenge
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button
                    onClick={() => handleCreateChallenge('conversation_starter')}
                    size="sm"
                    variant="outline"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10 h-auto py-3 flex-col"
                  >
                    <MessageSquare className="w-4 h-4 mb-1" />
                    <span className="text-xs">Conversation</span>
                    <span className="text-xs opacity-70">Beginner • 50pts</span>
                  </Button>
                  
                  <Button
                    onClick={() => handleCreateChallenge('active_listening')}
                    size="sm"
                    variant="outline"
                    className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 h-auto py-3 flex-col"
                  >
                    <Users className="w-4 h-4 mb-1" />
                    <span className="text-xs">Listening</span>
                    <span className="text-xs opacity-70">Intermediate • 75pts</span>
                  </Button>
                  
                  <Button
                    onClick={() => handleCreateChallenge('confidence_boost')}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-auto py-3 flex-col"
                  >
                    <Star className="w-4 h-4 mb-1" />
                    <span className="text-xs">Confidence</span>
                    <span className="text-xs opacity-70">Beginner • 100pts</span>
                  </Button>
                  
                  <Button
                    onClick={() => handleCreateChallenge('profile_refresh')}
                    size="sm"
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 h-auto py-3 flex-col"
                  >
                    <User className="w-4 h-4 mb-1" />
                    <span className="text-xs">Profile</span>
                    <span className="text-xs opacity-70">Intermediate • 125pts</span>
                  </Button>
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => handleCreateChallenge()}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Create Smart Challenge (Auto-Select)
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Active Challenges */}
            {active_challenges && active_challenges.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                  Your Active Challenges ({active_challenges.length})
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {active_challenges.map((challenge) => (
                    <Card key={challenge.challenge_id} className="bg-white/5 backdrop-blur-md border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center justify-between">
                          <span className="flex items-center">
                            <Target className="mr-2 text-orange-500" size={20} />
                            {challenge.title}
                          </span>
                          <Badge className={`${
                            challenge.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                            challenge.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                            'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {challenge.difficulty}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 text-sm mb-4">{challenge.description}</p>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center text-yellow-400">
                            <Award className="w-4 h-4 mr-1" />
                            <span className="text-sm">{challenge.points_reward} points</span>
                          </div>
                          
                          <div className="text-gray-400 text-xs">
                            Expires: {new Date(challenge.expires_at).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <Button 
                            size="sm" 
                            className="w-full bg-rose-600 hover:bg-rose-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Complete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {/* Show message if no challenges after creation options */}
            {(!active_challenges || active_challenges.length === 0) && (
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-8 text-center">
                  <Target className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Ready for Your First Challenge?</h3>
                  <p className="text-gray-300 mb-6">
                    Create a personalized challenge above to start earning coaching points!
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{dating_analytics.overall_stats.total_dating_sessions}</div>
                  <div className="text-gray-400 text-sm">Total Sessions</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <Heart className="w-8 h-8 text-red-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{dating_analytics.overall_stats.total_matches}</div>
                  <div className="text-gray-400 text-sm">Total Matches</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{dating_analytics.overall_stats.match_rate_percentage}%</div>
                  <div className="text-gray-400 text-sm">Match Rate</div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6 text-center">
                  <MessageSquare className="w-8 h-8 text-purple-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-white">{dating_analytics.overall_stats.session_completion_rate}%</div>
                  <div className="text-gray-400 text-sm">Completion Rate</div>
                </CardContent>
              </Card>
            </div>

            {/* Insights & Recommendations */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Lightbulb className="mr-2 text-yellow-500" size={20} />
                    Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dating_analytics.insights.map((insight, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-gray-300 text-sm">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Target className="mr-2 text-rose-500" size={20} />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dating_analytics.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-gray-300 text-sm">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachingDashboard;